/** usePropertyUtils.js
 * Composable для работы со свойствами (property) в SCADA-формах.
 * Заменяет методы MixinConverter, связанные с свойствами.
 */
import axios from 'axios';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import EventBus from '@/core/EventBus';

export function usePropertyUtils() {
  const store = useStore();
  const router = useRouter();
  const toast = useToast();

  // Поиск свойства по имени и фланцу
  function propertyByNameFlangeNo(props, name, flange) {
    return props[`${name}:${flange}`];
  }

  // Поиск свойства по URI и фланцу
  function propertyByURIFlangeNo(props, uri, flange) {
    return props[`${uri}:фл=${flange}`];
  }

  // Поиск свойства в массиве по имени
  function propertyByName(propsAll, sysName, name = 'sysName') {
    return propsAll.find(item => item[name] === sysName);
  }

  // Поиск компонента по имени (typeName + id)
  // Дублируем здесь, чтобы избежать циклической зависимости
  function componentByName(components, name) {
    for (const item of components) {
      if (item.typeName + item.id === name) {
        return item;
      }
    }
    store.dispatch("addScriptLog", {
      message: `Компонент не найден componentByName: ${name}`,
      facilityId: router.currentRoute.value.params.facility_id
    });
    return null;
  }

  // Привязка свойства к компоненту
  /*function propertyBind(component, prop, flange) {
    console.log(component, prop);

    if (!component || !prop) {
      store.dispatch("addScriptLog", {
        message: `Не удалось привязать свойство: ${prop}, id: ${component?.id}`,
        facilityId: router.currentRoute.value.params.facility_id
      });
      return false;
    }

    let newProp = null;

    if (!prop.typeName) {
      if (flange) {
        newProp = propertyByNameFlangeNo(store.getters.propertyAll, prop, flange);
      } else {
        newProp = propertyByName(store.getters.propertyAll, prop);
      }

      if (!newProp) {
        store.dispatch("addScriptLog", {
          message: `Свойство не найдено. Не удалось привязать свойство: ${prop}: ${flange}`,
          facilityId: router.currentRoute.value.params.facility_id
        });
        return false;
      }
      prop = newProp;
    }

    if (component.attrList) {
      if (!flange) {
        component.attrList.PropertyURI = `${prop.typeName}:фл=${String(prop.flangeNo)}`;
      } else {
        component.attrList.PropertyURI = `${prop.typeName}:фл=${String(flange)}`;
      }
    } else {
      let componentNew = componentByName(component, component);
      if (!componentNew) {
        store.dispatch("addScriptLog", {
          message: `Компонент не найден. Не удалось привязать свойство: ${prop}, ${flange}`,
          facilityId: router.currentRoute.value.params.facility_id
        });
        return false;
      }
      if (!flange) {
        componentNew.attrList.PropertyURI = `${prop.typeName}:фл=${String(prop.flangeNo)}`;
      } else {
        componentNew.attrList.PropertyURI = `${prop.typeName}:фл=${String(flange)}`;
      }
    }

    return true;
  }*/
  function propertyBind(components, props, component, prop, flange) {
    if (!component || !prop) {
      store.dispatch("addScriptLog", {
        message: `Не удалось привязать свойство: ${prop}, id: ${component?.id}`,
        facilityId: router.currentRoute.value.params.facility_id
      });
      return false;
    }
    if (!prop.typeName) {
      let newProp = null;

      if (flange) newProp = propertyByNameFlangeNo(props, prop, flange)
      else newProp = propertyByName(store.getters.propertyAll, prop);

      if (!newProp) {
        store.dispatch("addScriptLog", {
          message: `Свойство не найдено. Не удалось привязать свойство: ${prop}: ${flange}`,
          facilityId: router.currentRoute.value.params.facility_id
        }); return false;
      }
      prop = newProp;
    }
    if (component.attrList) {
      if (!flange) {
        component.attrList.PropertyURI = `${prop.typeName}:фл=${String(prop.flangeNo)}`;
      } else {
        component.attrList.PropertyURI = `${prop.typeName}:фл=${String(flange)}`;
      }
    } else {
      let componentNew = componentByName(components, component);
      if (!componentNew) {
        store.dispatch("addScriptLog", {
          message: `Компонент не найден. Не удалось привязать свойство: ${prop}, ${flange}`,
          facilityId: router.currentRoute.value.params.facility_id
        });
        return false;
      }
      if (!flange) {
        componentNew.attrList.PropertyURI = `${prop.typeName}:фл=${String(prop.flangeNo)}`;
      } else {
        componentNew.attrList.PropertyURI = `${prop.typeName}:фл=${String(flange)}`;
      }
    }
    return true;
  }
  // Отправка управляющего сигнала по фланцу
  function valueWrite(props, propName, flange, value) {
    const prop = propertyByNameFlangeNo(props, propName.trim(), flange);
    if (prop) {
      return propertyWrite(props, [], prop, value);
    } else {
      store.commit("SET_ERROR", {
        er: `Управляющее свойство "${propName}" не найдено для фланца ${flange}. Проверьте конфигурацию объекта.`,
        source: ['valueWrite']
      });
      store.dispatch("addScriptLog", {
        message: `Управляющее свойство "${propName}" не найдено для фланца ${flange}. Проверьте конфигурацию объекта.`,
        facilityId: router.currentRoute.value.params.facility_id
      });
      return;
    }
  }

  // Запись значения в свойство
  function write(sysName, value, flange = null) {
    let property;
    if (flange === null) {
      property = propertyByName(store.getters.propertyAll, sysName);
    } else {
      property = propertyByNameFlangeNo(
        store.getters.sysProp,
        sysName,
        flange
      );
    }

    if (!property) {
      store.commit("SET_ERROR", {
        er: `Свойство "${sysName}" не доступно для фланца ${flange || '-'}. Проверьте свойства у объекта.`,
        source: ['write']
      });
      store.dispatch("addScriptLog", {
        message: `Свойство "${sysName}" не доступно для фланца ${flange || '-'}. Проверьте свойства у объекта.`,
        facilityId: router.currentRoute.value.params.facility_id
      });
      return;
    }

    return propertyWrite({}, [], property, value);
  }

  // Получение свойства из БД
  function getPropertyDB(sysName, facilityId, activeFlangeNo) {
    try {
      facilityId = facilityId ?? store.getters.facilityCurrent?.id;
      if (!store.getters.propertySource) return;

      const prop = activeFlangeNo
        ? store.getters.propertySource.find(
          (item) =>
            item.facility_id === facilityId &&
            item.property_type_sysname === sysName &&
            item.active_flange_no === activeFlangeNo
        )
        : store.getters.propertySource.find(
          (item) =>
            item.facility_id === facilityId &&
            item.property_type_sysname === sysName
        );

      return prop;
    } catch (er) {
      store.dispatch("addScriptLog", {
        message: er,
        facilityId: router.currentRoute.value.params.facility_id
      });
    }
  }

  // Загрузка значения с учётом property_data_values
  function writeLoadedValue(sysName, loadedValue, flangeNo, defValue = 1) {
    const property = getPropertyDB(sysName);

    if (!property) {
      store.commit("SET_ERROR", {
        er: `Свойство "${sysName}" не найдено для фланца ${flangeNo}. Проверьте настройки объекта.`,
        source: ['writeLoadedValue']
      });
      store.dispatch("addScriptLog", {
        message: `Свойство "${sysName}" не найдено для фланца ${flangeNo}. Проверьте настройки объекта.`,
        facilityId: router.currentRoute.value.params.facility_id
      });
      return;
    }

    if (property.property_data_values?.[loadedValue]) {
      return write(sysName, property.property_data_values[loadedValue]);
    } else {
      return write(sysName, defValue, flangeNo);
    }
  }

  // Отправка управляющего сигнала
  function propertyWrite(propsObj, propsArray, prop, value, flange) {
    if (typeof prop === 'string') {
      if (flange) prop = propertyByNameFlangeNo(propsObj, prop, flange);
      else prop = propertyByName(propsArray, prop);
    }

    if (!prop) {
      store.commit("SET_ERROR", {
        er: `Свойство для фланца ${flange} не найдено в текущей конфигурации объекта.`,
        source: ['propertyWrite']
      });
      store.dispatch("addScriptLog", {
        message: `Свойство для фланца ${flange} не найдено в конфигурации.`,
        facilityId: router.currentRoute.value.params.facility_id
      });
      return;
    }

    if (!prop.path) {
      store.commit("SET_ERROR", {
        er: `Источник данных (path) не найден для свойства. Проверьте настройки подключения.`,
        source: ['propertyWrite']
      });
      store.dispatch("addScriptLog", {
        message: `Источник данных не найден для свойства. Проверьте конфигурацию.`,
        facilityId: router.currentRoute.value.params.facility_id
      });
      return;
    }

    if (value === undefined || value === null) return;

    const login = store.getters.info.login;
    const password = store.getters.password;
    const userID = store.getters.info.userId;

    const api = `${store.getters.server("TM_MIDDLE")}kron.tm3.service-web-1.0/webresources/restservice/property/${prop.id}/write/${value}/user/${userID}`;
    const headers = {
      Authorization: "Basic " + btoa(login + ":" + password),
    };

    return axios
      .get(api, { headers })
      .then(() => {
        toast.add({
          severity: "success",
          summary: "Управление отправлено",
          detail: "Значение успешно отправлено для свойства",
          life: 3000,
        });
      })
      .catch((er) => {
        store.commit("SET_ERROR", {
          er: er.response || er,
          source: ['propertyWrite']
        });
        store.dispatch("addScriptLog", {
          message: `Ошибка при отправке propertyWrite ${er.response || er}`,
          facilityId: router.currentRoute.value.params.facility_id
        });
      });
  }

  // Отправка переменной компоненту
  function sendVariableForComponent(componentName, variables) {
    EventBus.emit("on-send-variable", {
      componentName: componentName,
      variables: variables
    });
  }

  // Получение значения из компонента и запись в свойство
  function valueWriteFromComponent(props, components, propName, flange, component) {
    let componentName = null;
    if (typeof component === 'string') {
      const componentObj = componentByName(components, component);
      if (!componentObj) {
        store.commit("SET_ERROR", {
          er: `Компонент "${component}" не найден на схеме. Возможно, он был удалён или перемещён.`,
          source: ['valueWriteFromComponent']
        });
        store.dispatch("addScriptLog", {
          message: `Компонент "${component}" не найден на схеме. Проверьте конфигурацию диаграммы.`,
          facilityId: router.currentRoute.value.params.facility_id
        });
        return;
      }
      componentName = component;
    } else {
      componentName = component.typeName + component.id;
    }

    const value = onGetVariable(componentName, 'inputValue');
    if (value === null || value === undefined) {
      store.commit("SET_ERROR", {
        er: `Не указано новое значение для свойства "${propName}" (фланец ${flange}). Введите значение в поле ввода.`,
        source: ['valueWriteFromComponent']
      });
      store.dispatch("addScriptLog", {
        message: `Не задано новое значение для свойства "${propName}" на фланце ${flange}.`,
        facilityId: router.currentRoute.value.params.facility_id
      });
      return;
    }

    return valueWrite(props, propName, flange, value);
  }

  // Получение переменной от компонента
  function onGetVariable(componentName, variableName) {
    EventBus.emit("on-get-variable", {
      componentName: componentName,
      variableName: variableName
    });
    return store.getters.inputVariable;
  }

  // Выполнение хранимой процедуры
  function execProc(name, param) {
    const service = store.getters.nameToService(
      "BaseService",
      store.getters.info.login,
      store.getters.password,
      "-",
      "-"
    );
    return service.execProc(name, param);
  }

  // Определение типа значения
  function getType(value) {
    if (!isNaN(value)) {
      if (value % 1 === 0)
        return 3;
      if (value % 1 !== 0)
        return 5;
    } else {
      store.commit("SET_ERROR", {
        er: 'Введено некорректное значение. Допускаются только числовые значения.',
        source: 'getType',
      });
      store.dispatch("addScriptLog", {
        message: `Некорректное значение для преобразования типа: ${value}`,
        facilityId: router.currentRoute.value.params.facility_id
      });
    }
  }

  // Подготовка данных события свойства
  function preparePropertyEventData(events) {
    let event = {
      ["Последнее событие"]: {}
    };
    if (events.length && events.length > 0) {
      event["Последнее событие"] = {
        id: events[0].propertyId,
        dateProperty: events[0].date,
        flangeNo: events[0].flangeNo,
        isVisibleForm: false,
        outputValueColor: events[0]?.eventType?.color,
        outputValueText: `${events[0]?.eventType?.eventCategory.name}; ${events[0].date ? store.getters.typeFormat(new Date(events[0].date), 'date-time') : ''}; ${events[0]?.eventType?.name}`,
      };
    }
    return event;
  }

  // Подготовка события по имени
  function preparePropertyEvent(name) {
    return [{ [name]: "Последнее событие" }];
  }

  // Поиск свойства по событию
  function propertyByEvent(props, event) {
    if (!props) {
      store.dispatch("addScriptLog", {
        message: `Свойства не заданы для метода propertyByEvent: ${event}`,
        facilityId: router.currentRoute.value.params.facility_id
      });
      return null;
    }
    for (const prop in props) {
      if (props[prop].id === event.propertyId) {
        return props[prop];
      }
    }
  }

  return {
    propertyByNameFlangeNo,
    propertyByURIFlangeNo,
    propertyByName,
    propertyBind,
    valueWrite,
    write,
    getPropertyDB,
    writeLoadedValue,
    propertyWrite,
    sendVariableForComponent,
    valueWriteFromComponent,
    onGetVariable,
    execProc,
    getType,
    preparePropertyEventData,
    preparePropertyEvent,
    propertyByEvent,
  };
}
